/**
 * Layout helpers for section children: auto-organize grid and resize-to-fit bounds.
 */

import type { Node } from "@xyflow/react";

const MARGIN = 16;
const COL_GAP = 16;
const ROW_GAP = 16;
const GROUP_GAP = 32;
const RESIZE_TO_FIT_PADDING = 16;
const MIN_SECTION_WIDTH = 192;
const MIN_SECTION_HEIGHT = 144;

export type SectionLayoutMode = "tidy" | "horizontal" | "vertical" | "tile";

function classifyChildNode(node: Node): string {
  if (node.type === "textNote") return "note";
  if (node.type === "canvasText") return "label";
  if (node.type === "entity") {
    const w = node.measured?.width ?? node.width ?? 200;
    const h = node.measured?.height ?? node.height ?? 100;
    return w / h > 1.5 ? "entity" : "media";
  }
  return "other";
}

function colsForGroup(group: string, n: number): number {
  switch (group) {
    case "entity":
      return Math.min(3, Math.max(1, Math.ceil(Math.sqrt(n))));
    case "media":
      return Math.min(4, Math.max(1, Math.ceil(Math.sqrt(n))));
    case "note":
      return Math.min(2, Math.max(1, Math.ceil(Math.sqrt(n))));
    case "label":
      return Math.min(5, Math.max(1, Math.ceil(Math.sqrt(n * 2))));
    default:
      return Math.max(1, Math.ceil(Math.sqrt(n)));
  }
}

export function computeAutoOrganizeLayout(
  sectionId: string,
  children: Node[],
): {
  positionUpdates: Array<{
    nodeId: string;
    parentId: string;
    position: { x: number; y: number };
  }>;
  sectionWidth: number;
  sectionHeight: number;
} | null {
  if (children.length === 0) return null;

  const GROUP_ORDER = ["entity", "media", "note", "label", "other"];
  const byGroup = new Map<string, Node[]>();
  for (const node of children) {
    const key = classifyChildNode(node);
    if (!byGroup.has(key)) byGroup.set(key, []);
    byGroup.get(key)!.push(node);
  }

  const groups = GROUP_ORDER.filter((g) => byGroup.has(g)).map((g) => ({
    name: g,
    nodes: byGroup.get(g)!,
  }));

  const positionUpdates: Array<{
    nodeId: string;
    parentId: string;
    position: { x: number; y: number };
  }> = [];
  let currentY = MARGIN;
  let sectionWidth = 0;

  for (const { name, nodes: groupNodes } of groups) {
    const sorted = [...groupNodes].sort((a, b) => {
      const na = String(
        (a.data as { title?: string; content?: string })?.title ??
          (a.data as { content?: string })?.content ??
          "",
      );
      const nb = String(
        (b.data as { title?: string; content?: string })?.title ??
          (b.data as { content?: string })?.content ??
          "",
      );
      return na.localeCompare(nb);
    });

    const COLS = colsForGroup(name, sorted.length);
    const ROWS = Math.ceil(sorted.length / COLS);

    const colWidths = Array.from({ length: COLS }, (_, c) =>
      Math.max(
        ...sorted
          .filter((_, i) => i % COLS === c)
          .map((n) => n.measured?.width ?? n.width ?? 200),
      ),
    );

    const rowHeights = Array.from({ length: ROWS }, (_, r) =>
      Math.max(
        ...sorted
          .slice(r * COLS, (r + 1) * COLS)
          .map((n) => n.measured?.height ?? n.height ?? 100),
      ),
    );

    const colX = colWidths.reduce<number[]>((acc, _w, i) => {
      acc.push(i === 0 ? MARGIN : acc[i - 1] + colWidths[i - 1] + COL_GAP);
      return acc;
    }, []);

    const rowY = rowHeights.reduce<number[]>((acc, _h, r) => {
      acc.push(r === 0 ? 0 : acc[r - 1] + rowHeights[r - 1] + ROW_GAP);
      return acc;
    }, []);

    sorted.forEach((node, i) => {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      positionUpdates.push({
        nodeId: node.id,
        parentId: sectionId,
        position: { x: colX[col], y: currentY + rowY[row] },
      });
    });

    sectionWidth = Math.max(
      sectionWidth,
      colX[COLS - 1] + colWidths[COLS - 1] + MARGIN,
    );
    currentY += rowY[ROWS - 1] + rowHeights[ROWS - 1] + GROUP_GAP;
  }

  return {
    positionUpdates,
    sectionWidth: Math.max(MIN_SECTION_WIDTH, sectionWidth),
    sectionHeight: Math.max(MIN_SECTION_HEIGHT, currentY - GROUP_GAP + MARGIN),
  };
}

function nodeSize(node: Node) {
  return {
    w: node.measured?.width ?? node.width ?? 200,
    h: node.measured?.height ?? node.height ?? 100,
  };
}

export function computeSectionLayoutByMode(
  sectionId: string,
  children: Node[],
  mode: SectionLayoutMode,
): ReturnType<typeof computeAutoOrganizeLayout> {
  if (children.length === 0) return null;
  if (mode === "tidy") {
    return computeAutoOrganizeLayout(sectionId, children);
  }

  const sorted = [...children].sort((a, b) => a.id.localeCompare(b.id));
  const positionUpdates: Array<{
    nodeId: string;
    parentId: string;
    position: { x: number; y: number };
  }> = [];

  if (mode === "horizontal") {
    let x = MARGIN;
    let maxH = 0;
    for (const node of sorted) {
      const { w, h } = nodeSize(node);
      positionUpdates.push({
        nodeId: node.id,
        parentId: sectionId,
        position: { x, y: MARGIN },
      });
      x += w + COL_GAP;
      maxH = Math.max(maxH, h);
    }
    return {
      positionUpdates,
      sectionWidth: Math.max(MIN_SECTION_WIDTH, x - COL_GAP + MARGIN),
      sectionHeight: Math.max(MIN_SECTION_HEIGHT, maxH + MARGIN * 2),
    };
  }

  if (mode === "vertical") {
    let y = MARGIN;
    let maxW = 0;
    for (const node of sorted) {
      const { w, h } = nodeSize(node);
      positionUpdates.push({
        nodeId: node.id,
        parentId: sectionId,
        position: { x: MARGIN, y },
      });
      y += h + ROW_GAP;
      maxW = Math.max(maxW, w);
    }
    return {
      positionUpdates,
      sectionWidth: Math.max(MIN_SECTION_WIDTH, maxW + MARGIN * 2),
      sectionHeight: Math.max(MIN_SECTION_HEIGHT, y - ROW_GAP + MARGIN),
    };
  }

  // tile
  const cols = Math.max(1, Math.ceil(Math.sqrt(sorted.length)));
  const colWidths = Array.from({ length: cols }, (_, c) =>
    Math.max(
      ...sorted.filter((_, i) => i % cols === c).map((n) => nodeSize(n).w),
    ),
  );
  const rows = Math.ceil(sorted.length / cols);
  const rowHeights = Array.from({ length: rows }, (_, r) =>
    Math.max(
      ...sorted.slice(r * cols, (r + 1) * cols).map((n) => nodeSize(n).h),
    ),
  );
  const colX = colWidths.reduce<number[]>((acc, _w, i) => {
    acc.push(i === 0 ? MARGIN : acc[i - 1] + colWidths[i - 1] + COL_GAP);
    return acc;
  }, []);
  const rowY = rowHeights.reduce<number[]>((acc, _h, r) => {
    acc.push(r === 0 ? MARGIN : acc[r - 1] + rowHeights[r - 1] + ROW_GAP);
    return acc;
  }, []);

  sorted.forEach((node, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    positionUpdates.push({
      nodeId: node.id,
      parentId: sectionId,
      position: { x: colX[col], y: rowY[row] },
    });
  });

  return {
    positionUpdates,
    sectionWidth: Math.max(
      MIN_SECTION_WIDTH,
      colX[cols - 1] + colWidths[cols - 1] + MARGIN,
    ),
    sectionHeight: Math.max(
      MIN_SECTION_HEIGHT,
      rowY[rows - 1] + rowHeights[rows - 1] + MARGIN,
    ),
  };
}

export function computeResizeToFitLayout(
  sectionId: string,
  children: Node[],
  /** Section `position` — parent-relative when nested, same as canvas coords when root. */
  sectionPosition: { x: number; y: number },
): {
  positionUpdates: Array<{
    nodeId: string;
    parentId: string;
    position: { x: number; y: number };
  }>;
  sectionPosition: { x: number; y: number };
  sectionWidth: number;
  sectionHeight: number;
} | null {
  if (children.length === 0) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxRight = -Infinity;
  let maxBottom = -Infinity;

  for (const child of children) {
    const w = child.measured?.width ?? child.width ?? 200;
    const h = child.measured?.height ?? child.height ?? 100;
    minX = Math.min(minX, child.position.x);
    minY = Math.min(minY, child.position.y);
    maxRight = Math.max(maxRight, child.position.x + w);
    maxBottom = Math.max(maxBottom, child.position.y + h);
  }

  const boxWidth = Math.max(
    MIN_SECTION_WIDTH,
    maxRight - minX + RESIZE_TO_FIT_PADDING * 2,
  );
  const boxHeight = Math.max(
    MIN_SECTION_HEIGHT,
    maxBottom - minY + RESIZE_TO_FIT_PADDING * 2,
  );
  const offsetX = minX - RESIZE_TO_FIT_PADDING;
  const offsetY = minY - RESIZE_TO_FIT_PADDING;

  return {
    positionUpdates: children.map((child) => ({
      nodeId: child.id,
      parentId: sectionId,
      position: {
        x: child.position.x - offsetX,
        y: child.position.y - offsetY,
      },
    })),
    sectionPosition: {
      x: sectionPosition.x + offsetX,
      y: sectionPosition.y + offsetY,
    },
    sectionWidth: boxWidth,
    sectionHeight: boxHeight,
  };
}
